import { FormEvent, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrg } from "@/hooks/useOrg";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function CreateOrg() {
  const { user, loading: authLoading } = useAuth();
  const { refetch, switchOrg, loading: orgLoading } = useOrg();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // const defaultOrgName = useMemo(() => {
  //   const firstName = user?.user_metadata?.full_name?.split(" ")[0] || "My";
  //   return `${firstName}'s Team`;
  // }, [user?.user_metadata?.full_name]);

  // useEffect(() => {
  //   if (!orgName) setOrgName(defaultOrgName);
  // }, [defaultOrgName, orgName]);

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user || !orgName.trim() || submitting) return;

    setSubmitting(true);
    const orgId = crypto.randomUUID();

    const { error: orgError } = await supabase
      .from("organizations")
      .insert({ id: orgId, name: orgName.trim(), created_by: user.id });

    if (orgError) {
      toast.error(orgError.message);
      setSubmitting(false);
      return;
    }

    const { error: memberError } = await supabase.from("org_members").insert({
      org_id: orgId,
      user_id: user.id,
      role: "owner",
      accepted_at: new Date().toISOString(),
    });

    if (memberError) {
      toast.error(memberError.message);
      setSubmitting(false);
      return;
    }

    await refetch();
    switchOrg(orgId);
    navigate("/dashboard");
  };

  if (authLoading || orgLoading) return null;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <form
        onSubmit={handleCreate}
        className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-6"
      >
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-foreground">Create team</h1>
          <p className="text-sm text-muted-foreground">
            Choose a name for your new team.
          </p>
        </div>

        <Input
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          placeholder="Organization name"
          autoFocus
        />

        <Button
          type="submit"
          className="w-full"
          disabled={submitting || !orgName.trim()}
        >
          {submitting ? "Creating…" : "Create team"}
        </Button>
      </form>
    </div>
  );
}
